// Supabase client setup and shared types used across the app
import { createClient } from "@supabase/supabase-js";

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseKey = import.meta.env.DEV
  ? import.meta.env.VITE_SUPABASE_ANON_KEY
  : import.meta.env.VITE_SUPABASE_PUBLISHABLE_DEFAULT_KEY;

if (!supabaseUrl || !supabaseKey) {
  throw new Error("Missing Supabase environment variables.");
}

// Wrap fetch with a timeout so requests never hang indefinitely (e.g. after a
// mobile PWA wakes from suspension).  If the caller already supplied an
// AbortSignal we combine both so whichever fires first wins.
const fetchWithTimeout: typeof fetch = (input, init) => {
  const timeoutSignal = AbortSignal.timeout(10_000);
  const signal = init?.signal
    ? AbortSignal.any([init.signal as AbortSignal, timeoutSignal])
    : timeoutSignal;
  return fetch(input, { ...init, signal });
};

export const supabase = createClient(supabaseUrl, supabaseKey, {
  global: { fetch: fetchWithTimeout },
  realtime: {
    params: {
      eventsPerSecond: 10,
    },
    heartbeatIntervalMs: 30000,
    timeout: 20000,
  },
  auth: {
    persistSession: true,
    autoRefreshToken: true,
  },
});

export type InventoryItem = {
  id: string;
  user_id: string;
  household_id: string;
  item_name: string;
  quantity: number | null;
  unit: string | null;
  location: "fridge" | "freezer" | "pantry" | "other";
  category: string | null;
  created_at: string;
  updated_at: string;
};

export type FavoriteRecipe = {
  id: string;
  household_id: string;
  added_by: string;
  recipe_id: number;
  title: string;
  image: string | null;
  recipe_data: import("../services/recipeService").RecipeDetails;
  created_at: string;
};

export type ShoppingListItem = {
  id: string;
  user_id: string;
  household_id: string;
  item_name: string;
  quantity: number | null;
  unit: string | null;
  category?: string;
  purchased: boolean;
  created_at: string;
  updated_at: string;
};
